/**
 * EnergyChartThreePhaseBase - 三相電專用
 * Slider 預設總計，可滑動切換 total → r → s → t → total
 * Style03: AreaChart, Style04: BarChart
 */
import { useState, useMemo, useRef, useEffect } from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  PHASE_ORDER,
  PHASE_LABELS,
  PHASE_COLORS,
  type PhaseKey,
  type ThreePhaseMetricId,
  THREE_PHASE_METRIC_CONFIG,
} from './threePhaseSensorConfig'
import {
  querySensorReportWithAuthContext,
  type GenerateReportDeviceData,
} from '../../services/turingApi'
import { useEnergyDeviceSnapshot, type EnergyDeviceSnapshot } from './useEnergyDeviceSnapshot'
import './EnergyChart.css'

export type TimeRange = '1H' | '24H' | '7D' | '30D'

const TIME_RANGES: { id: TimeRange; label: string }[] = [
  { id: '1H', label: '1H' },
  { id: '24H', label: '24H' },
  { id: '7D', label: '7D' },
  { id: '30D', label: '30D' },
]

type ThreePhaseDataPoint = { time: string; total: number; r: number; s: number; t: number }

function formatTimeLabel(timestamp: number, range: TimeRange): string {
  const t = new Date(timestamp)
  if (range === '1H' || range === '24H') {
    return t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }
  return t.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
}

function toTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : null
}

function toNumber(value: unknown): number | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    return toNumber(value[0])
  }
  if (value && typeof value === 'object') {
    const payload = value as Record<string, unknown>
    if ('value' in payload) return toNumber(payload.value)
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  }
  return null
}

function mapTimeRangeToGenerateReport(range: TimeRange): {
  duration: string
  granularity: 'minute' | 'hour' | 'day'
} {
  if (range === '1H') return { duration: '1h', granularity: 'minute' }
  if (range === '24H') return { duration: '24h', granularity: 'hour' }
  if (range === '7D') return { duration: '7d', granularity: 'day' }
  return { duration: '30d', granularity: 'day' }
}

function resolveReportDeviceData(
  reportData: Record<string, GenerateReportDeviceData> | undefined,
  deviceSN: string
): GenerateReportDeviceData | null {
  if (!reportData) return null
  if (reportData[deviceSN]) return reportData[deviceSN]
  const baseSN = String(deviceSN || '').split('#')[0]
  if (reportData[baseSN]) return reportData[baseSN]
  for (const device of Object.values(reportData)) {
    if (!device) continue
    if (device.deviceSN === deviceSN) return device
    if (device.baseSN === baseSN) return device
  }
  return null
}

function pickPointMetricValue(
  point: Record<string, unknown>,
  metric: ThreePhaseMetricId,
  phase: PhaseKey
): number | null {
  const keyList =
    metric === 'real_power'
      ? {
          total: ['real_power_total', 'watt_total', 'real_power', 'watt', 'power'],
          r: ['real_power_r', 'watt_r', 'power_r'],
          s: ['real_power_s', 'watt_s', 'power_s'],
          t: ['real_power_t', 'watt_t', 'power_t'],
        }
      : {
          total: ['watt_hour_total', 'watt_hour', 'energy'],
          r: ['watt_hour_r', 'energy_r'],
          s: ['watt_hour_s', 'energy_s'],
          t: ['watt_hour_t', 'energy_t'],
        }

  for (const key of keyList[phase]) {
    const value = toNumber(point[key])
    if (value !== null) return value
  }
  return null
}

function buildFallbackDataFromSnapshot(
  range: TimeRange,
  values: { total: number | null; r: number | null; s: number | null; t: number | null }
): ThreePhaseDataPoint[] {
  const now = Date.now()
  const points = range === '1H' ? 12 : range === '24H' ? 24 : range === '7D' ? 7 : 30
  const intervalMs =
    range === '1H'
      ? 5 * 60 * 1000
      : range === '24H'
        ? 60 * 60 * 1000
        : 24 * 60 * 60 * 1000
  const total = values.total ?? 0
  const r = values.r ?? 0
  const s = values.s ?? 0
  const t = values.t ?? 0
  const data: ThreePhaseDataPoint[] = []
  for (let i = points; i >= 0; i--) {
    const timestamp = now - i * intervalMs
    data.push({
      time: formatTimeLabel(timestamp, range),
      total,
      r,
      s,
      t,
    })
  }
  return data
}

function resolveSnapshotMetricValues(
  snapshot: EnergyDeviceSnapshot | null,
  metric: ThreePhaseMetricId
): { total: number | null; r: number | null; s: number | null; t: number | null } | null {
  if (!snapshot) return null
  if (metric === 'real_power') {
    return {
      total: snapshot.totalPower,
      r: snapshot.phases.r.power,
      s: snapshot.phases.s.power,
      t: snapshot.phases.t.power,
    }
  }
  return {
    total: snapshot.totalEnergy,
    r: snapshot.phases.r.energy,
    s: snapshot.phases.s.energy,
    t: snapshot.phases.t.energy,
  }
}

function generateMockThreePhaseData(range: TimeRange): ThreePhaseDataPoint[] {
  const now = Date.now()
  const points = range === '1H' ? 12 : range === '24H' ? 24 : range === '7D' ? 7 : 30
  const intervalMs =
    range === '1H'
      ? 5 * 60 * 1000
      : range === '24H'
        ? 60 * 60 * 1000
        : range === '7D'
          ? 24 * 60 * 60 * 1000
          : 24 * 60 * 60 * 1000

  const data: ThreePhaseDataPoint[] = []
  let baseTotal = 500
  for (let i = points; i >= 0; i--) {
    const timestamp = now - i * intervalMs
    baseTotal += (Math.random() - 0.5) * 4
    baseTotal = Math.max(400, Math.min(600, baseTotal))
    const r = Math.round((baseTotal / 3) * (0.9 + Math.random() * 0.2) * 10) / 10
    const s = Math.round((baseTotal / 3) * (0.9 + Math.random() * 0.2) * 10) / 10
    const tVal = Math.round((baseTotal - r - s) * 10) / 10
    const total = Math.round(baseTotal * 10) / 10
    const timeStr = formatTimeLabel(timestamp, range)
    data.push({ time: timeStr, total, r, s, t: tVal })
  }
  return data
}

export type EnergyChartVariant = 'blue' | 'gold'
export type EnergyChartType = 'area' | 'bar'

export interface EnergyChartThreePhaseBaseProps {
  variant: EnergyChartVariant
  chartType: EnergyChartType
  deviceSN?: string | null
  credentials?: { apiKey?: string; accessToken?: string; appId?: string }
}

const VARIANT_STYLES: Record<
  EnergyChartVariant,
  {
    stroke: string
    fill: string
    gradientId: string
    gradientFrom: string
    gradientTo: string
    dropdownActiveClass: string
  }
> = {
  blue: {
    stroke: '#38bdf8',
    fill: '#38bdf8',
    gradientId: 'energy3pGradient-blue',
    gradientFrom: '#38bdf8',
    gradientTo: '#38bdf8',
    dropdownActiveClass: 'energy-chart__dropdown--blue',
  },
  gold: {
    stroke: '#fbbf24',
    fill: '#fbbf24',
    gradientId: 'energy3pGradient-gold',
    gradientFrom: '#fbbf24',
    gradientTo: '#fbbf24',
    dropdownActiveClass: 'energy-chart__dropdown--gold',
  },
}

export function EnergyChartThreePhaseBase({
  variant,
  chartType,
  deviceSN,
  credentials,
}: EnergyChartThreePhaseBaseProps) {
  const hasRealtimeSource = Boolean(
    String(deviceSN || '').trim() && (credentials?.apiKey || credentials?.accessToken)
  )
  const { snapshot, loading: snapshotLoading } = useEnergyDeviceSnapshot(deviceSN, credentials, {
    previewFallback: !hasRealtimeSource,
  })
  const [range, setRange] = useState<TimeRange>('24H')
  const [metric, setMetric] = useState<ThreePhaseMetricId>('watt_hour')
  const [slideIndex, setSlideIndex] = useState(0) // 0=total, 1=r, 2=s, 3=t
  const [data, setData] = useState<ThreePhaseDataPoint[]>(() => generateMockThreePhaseData('24H'))
  const [loading, setLoading] = useState<boolean>(hasRealtimeSource)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    if (!hasRealtimeSource) {
      setData(generateMockThreePhaseData(range))
      setLoading(false)
      return () => {
        cancelled = true
      }
    }

    const sn = String(deviceSN || '').trim()
    if (!sn) {
      setData([])
      setLoading(false)
      return () => {
        cancelled = true
      }
    }

    setLoading(true)
    const reportScope = mapTimeRangeToGenerateReport(range)
    querySensorReportWithAuthContext(
      {
        apiKey: credentials?.apiKey,
        accessToken: credentials?.accessToken,
        appId: credentials?.appId,
      },
      sn,
      {
        duration: reportScope.duration,
        granularity: reportScope.granularity,
        metrics: 'auto',
        format: 'summary',
      }
    )
      .then((reportResponse) => {
        if (cancelled) return
        const deviceData = resolveReportDeviceData(reportResponse.data, sn)
        const rows = Array.isArray(deviceData?.chartData) ? deviceData.chartData : []
        const parsed = rows
          .map((row) => {
            const point = row as Record<string, unknown>
            const time = toTimestamp(point.time)
            if (time === null) return null
            const r = pickPointMetricValue(point, metric, 'r')
            const s = pickPointMetricValue(point, metric, 's')
            const t = pickPointMetricValue(point, metric, 't')
            const total = pickPointMetricValue(point, metric, 'total')
            const totalFromPhases =
              r !== null || s !== null || t !== null
                ? (r ?? 0) + (s ?? 0) + (t ?? 0)
                : null
            const resolvedTotal = total ?? totalFromPhases
            if (resolvedTotal === null && r === null && s === null && t === null) return null
            return {
              time: formatTimeLabel(time, range),
              total: resolvedTotal ?? 0,
              r: r ?? 0,
              s: s ?? 0,
              t: t ?? 0,
            } satisfies ThreePhaseDataPoint
          })
          .filter((item): item is ThreePhaseDataPoint => item !== null)

        if (parsed.length > 0) {
          setData(parsed)
          return
        }

        const snapshotMetricValues = resolveSnapshotMetricValues(snapshot, metric)
        if (snapshotMetricValues) {
          setData(buildFallbackDataFromSnapshot(range, snapshotMetricValues))
        } else {
          setData([])
        }
      })
      .catch(() => {
        if (cancelled) return
        const snapshotMetricValues = resolveSnapshotMetricValues(snapshot, metric)
        if (snapshotMetricValues) {
          setData(buildFallbackDataFromSnapshot(range, snapshotMetricValues))
        } else {
          setData([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    credentials?.accessToken,
    credentials?.apiKey,
    credentials?.appId,
    deviceSN,
    hasRealtimeSource,
    metric,
    range,
    snapshot,
  ])

  const phase = PHASE_ORDER[slideIndex] as PhaseKey
  const dataKey = phase
  const styles = VARIANT_STYLES[variant]
  const cfg = THREE_PHASE_METRIC_CONFIG[metric]

  const stats = useMemo(() => {
    if (data.length === 0) return { current: null, max: null, min: null }
    const vals = data.map((d) => d[dataKey])
    const current = vals[vals.length - 1] ?? 0
    const max = Math.max(...vals)
    const min = Math.min(...vals)
    return { current, max, min }
  }, [data, dataKey])

  const formatY = (v: number) => `${v}`
  const formatTooltip = (value: number | string, p: PhaseKey): [string, string] => {
    const numeric = typeof value === 'number' ? value : Number(value || 0)
    return [`${numeric}${cfg.unit}`, PHASE_LABELS[p]]
  }

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const width = el.clientWidth
    const index = Math.round(el.scrollLeft / width)
    const clamped = Math.max(0, Math.min(3, index))
    setSlideIndex(clamped)
  }

  if (loading || (snapshotLoading && data.length === 0)) {
    return (
      <div className={`energy-chart energy-chart--${variant} energy-chart--three-phase`}>
        <div className="energy-chart__loading">Loading sensors...</div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={`energy-chart energy-chart--${variant} energy-chart--three-phase`}>
        <div className="energy-chart__loading">No sensor data</div>
      </div>
    )
  }

  const chartMargin = { top: 8, right: 8, left: 8, bottom: 8 }
  const axisStyle = {
    tick: { fill: 'rgba(148,163,184,0.8)', fontSize: 10 },
    axisLine: { stroke: 'rgba(255,255,255,0.2)' },
    tickLine: { stroke: 'rgba(255,255,255,0.2)' },
  }
  const tooltipStyle = {
    contentStyle: {
      background: 'rgba(15,23,42,0.95)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 8,
    },
    labelStyle: { color: '#94a3b8' },
  }

  return (
    <div className={`energy-chart energy-chart--${variant} energy-chart--three-phase`}>
      <div className="energy-chart__controls">
        <div className="energy-chart__controls-left">
          <div className={`energy-chart__dropdown ${styles.dropdownActiveClass}`}>
            <span className="energy-chart__dropdown-icon material-symbols-rounded" aria-hidden>schedule</span>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as TimeRange)}
              className="energy-chart__select"
              aria-label="Time range"
            >
              {TIME_RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className={`energy-chart__dropdown ${styles.dropdownActiveClass}`}>
            <span className="energy-chart__dropdown-icon material-symbols-rounded" aria-hidden>electric_bolt</span>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as ThreePhaseMetricId)}
              className="energy-chart__select"
              aria-label="Metric"
            >
              {Object.values(THREE_PHASE_METRIC_CONFIG).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="energy-chart__phase-indicator">
          <span
            className="energy-chart__phase-icon"
            style={{ ['--phase-color' as string]: PHASE_COLORS[phase] }}
            aria-current="true"
          >
            {PHASE_LABELS[phase]}
          </span>
        </div>
      </div>

      <div className="energy-chart__slider">
        <div className="energy-chart__slider-hint" aria-hidden>
          <span className="energy-chart__slider-hint-icon material-symbols-rounded">swipe_right</span>
        </div>
        <div
          ref={scrollRef}
          className="energy-chart__slider-track"
          onScroll={handleScroll}
          role="region"
          aria-label="Swipe to switch Total, R, S, T"
        >
          {PHASE_ORDER.map((p) => (
            <div key={p} className="energy-chart__slider-slide">
              <div
                className="energy-chart__chart"
                data-css-slot="chartLine"
                data-css-slot-label="Chart Line"
              >
                <ResponsiveContainer width="100%" height={180}>
                  {chartType === 'area' ? (
                    <AreaChart data={data} margin={chartMargin}>
                      <defs>
                        <linearGradient id={`${styles.gradientId}-${p}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={styles.gradientFrom} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={styles.gradientTo} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="time" {...axisStyle} />
                      <YAxis domain={['dataMin - 2', 'dataMax + 2']} {...axisStyle} tickFormatter={formatY} />
                      <Tooltip {...tooltipStyle} formatter={(value) => formatTooltip(value, p)} />
                      <Area
                        type="monotone"
                        dataKey={p}
                        stroke={styles.stroke}
                        strokeWidth={2}
                        fill={`url(#${styles.gradientId}-${p})`}
                      />
                    </AreaChart>
                  ) : (
                    <BarChart data={data} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="time" {...axisStyle} />
                      <YAxis domain={['dataMin - 2', 'dataMax + 2']} {...axisStyle} tickFormatter={formatY} />
                      <Tooltip
                        {...tooltipStyle}
                        cursor={{ fill: 'transparent', stroke: styles.stroke, strokeWidth: 2 }}
                        formatter={(value) => formatTooltip(value, p)}
                      />
                      <Bar dataKey={p} fill={styles.fill} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="energy-chart__stats">
        <span>Now {stats.current ?? '--'}{cfg.unit}</span>
        <span>Max {stats.max ?? '--'}{cfg.unit}</span>
        <span>Min {stats.min ?? '--'}{cfg.unit}</span>
      </div>
    </div>
  )
}
